// src/components/PassiveSearch/PassiveSearchView.jsx

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Paper,
    IconButton,
    InputBase,
    Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const PassiveSearchView = ({
    jobDescription,
    onJobDescriptionChange,
    onSubmit,
    isProcessing,
    error,
    onHowSearchWorksClick,
    searchHistory = [],
}) => {

    const location = useLocation();
    const [currentJobDescription, setCurrentJobDescription] = useState('');

    useEffect(() => {
        const initialDescFromLocation = location.state?.jobDescription;

        if (initialDescFromLocation) {
            setCurrentJobDescription(initialDescFromLocation);
            
        } else if (jobDescription !== undefined && jobDescription !== currentJobDescription) {
            setCurrentJobDescription(jobDescription);
        }
    }, [location.state, jobDescription]);

    const handleDescriptionChange = (event) => {
        const newValue = event.target.value;
        setCurrentJobDescription(newValue);
        if (onJobDescriptionChange) {
            onJobDescriptionChange(newValue);
        }
    };

    const handleSubmit = () => {
        if (onSubmit) {
            onSubmit(currentJobDescription);
        }
    };
    
    const handleHistoryClick = (query) => {
        setCurrentJobDescription(query);
        if (onSubmit) {
            onSubmit(query);
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                bgcolor: 'transparent',
            }}
        >
            <Box
                component="img"
                src="/ct_logo_black.png"
                alt="Passive Candidates Logo"
                style={{ display: 'var(--logo-light-display)', width: 'auto', height: '120px', maxHeight: '120px', marginBottom: '24px' }}
            />
            <Box
                component="img"
                src="/ct_logo.png"
                alt="Passive Candidates Logo"
                style={{ display: 'var(--logo-dark-display)', width: 'auto', height: '120px', maxHeight: '120px', marginBottom: '24px' }}
            />

            <Paper
                elevation={0}
                component="form"
                data-tour="search-textarea"
                onSubmit={handleSubmit}
                sx={{
                    py: '0px', px: '8px', display: 'flex', alignItems: 'center', width: '100%', mb: 2.5,
                    borderRadius: '28px', 
                    bgcolor: 'background.paper', 
                    border: 1, 
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'action.hover' },
                }}
            >
                <IconButton sx={{ p: '10px' }} aria-label="search icon" onClick={handleSubmit} disabled={isProcessing}>
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: '1.25rem' }} />
                </IconButton>
                <InputBase
                    sx={{ pt:3 ,ml: 0.5, flex: 1, fontSize: '0.95rem', color: 'text.primary', alignSelf: 'stretch', display: 'flex' }}
                    placeholder="Describe your ideal candidate..."
                    value={currentJobDescription}
                    onChange={handleDescriptionChange}
                    disabled={isProcessing}
                    multiline
                    minRows={1}
                    inputComponent="textarea"
                />
            </Paper>

            {searchHistory.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', alignSelf: 'center' }}>Recent:</Typography>
                    {searchHistory.map((query, index) => (
                        <Chip
                            key={index}
                            label={query.substring(0, 30) + (query.length > 30 ? '...' : '')}
                            onClick={() => handleHistoryClick(query)}
                            size="small"
                        />
                    ))}
                </Box>
            )}

            {error && <Typography color="error" variant="body2" sx={{ mb: 2, textAlign: 'center' }}>{error}</Typography>}

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: '12px', mb: 6 }}>
                <Button 
                    type="submit" 
                    variant="contained" 
                    color="primary" 
                    disabled={isProcessing || !currentJobDescription.trim()} 
                    onClick={handleSubmit} 
                    data-tour="find-candidates-button"
                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                >
                    Find Top Candidates
                </Button>
                <Button 
                    variant="outlined" 
                    color="inherit" 
                    onClick={onHowSearchWorksClick} 
                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                >
                    How search works
                </Button>
            </Box>
        </Paper>
    );
};

export default PassiveSearchView;